import './App.css';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GiWeightLiftingUp } from 'react-icons/gi';
import DashboardHeader from './dashboardheader.js';
import VerticalNavigation from './VerticalNavigation.js';
import bhcalc from './bhcalc.js';

function EditLocationModal({ location, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: location.name || '',
    address: location.address || '',
    zipcode: location.zipcode || '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(location.logoUrl || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview(location.logoUrl || null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('address', formData.address);
      fd.append('zipcode', formData.zipcode);
      if (logoFile) fd.append('logo', logoFile);
      await bhcalc.put(`/location/${location.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
    } catch (err) {
      setError('Failed to update location.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Edit Location Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="w-24 shrink-0 text-sm font-medium text-gray-700 text-right">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="w-24 shrink-0 text-sm font-medium text-gray-700 text-right">Address *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="w-24 shrink-0 text-sm font-medium text-gray-700 text-right">Zipcode *</label>
            <input
              type="text"
              name="zipcode"
              value={formData.zipcode}
              onChange={handleChange}
              required
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-start gap-3">
            <label className="w-24 shrink-0 text-sm font-medium text-gray-700 text-right pt-2">Logo</label>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {logoPreview && (
                <img src={logoPreview} alt="Logo preview" className="mt-2 max-h-20 rounded border border-gray-200" />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddProgramModal({ locationId, onClose, onAdded }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    bhcalc.get('/program')
      .then(({ data }) => setPrograms(data.filter(p => !p.isArchived)))
      .catch(() => setError('Failed to load programs.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProgramId) return;
    setSubmitting(true);
    setError(null);
    try {
      await bhcalc.post('/program-location', {
        programId: parseInt(selectedProgramId),
        locationId: parseInt(locationId),
      });
      onAdded();
    } catch (err) {
      setError('Failed to add program. It may already be assigned to this location.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Add Program to Location</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Program
            </label>
            {loading ? (
              <p className="text-sm text-gray-500">Loading programs...</p>
            ) : programs.length === 0 ? (
              <p className="text-sm text-gray-500">No active programs available.</p>
            ) : (
              <select
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- Choose a program --</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.programName}{p.programType ? ` (${p.programType.name})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedProgramId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Adding...' : 'Add Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProgramLocationModal({ locationProgram, onClose, onSaved }) {
  const program = locationProgram.program;

  const [startDateOverride, setStartDateOverride] = useState(locationProgram.startDateOverride || '');
  const [endDateOverride, setEndDateOverride] = useState(locationProgram.endDateOverride || '');
  const [isArchivedOverride, setIsArchivedOverride] = useState(
    locationProgram.isArchivedOverride === null || locationProgram.isArchivedOverride === undefined
      ? 'inherit'
      : locationProgram.isArchivedOverride ? 'archived' : 'active'
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await bhcalc.put(`/program-location/${locationProgram.id}`, {
        startDateOverride: startDateOverride || null,
        endDateOverride: endDateOverride || null,
        isArchivedOverride: isArchivedOverride === 'inherit' ? null : isArchivedOverride === 'archived',
      });
      onSaved();
    } catch (err) {
      setError('Failed to save overrides.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-800">Edit Program Overrides</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          <span className="font-medium text-gray-700">{program?.programName}</span>
          {program?.programType ? ` · ${program.programType.name}` : ''}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date Override</label>
            <p className="text-xs text-gray-400 mb-1">Program default: {program?.startDate || 'none'}</p>
            <input
              type="date"
              value={startDateOverride}
              onChange={(e) => setStartDateOverride(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {startDateOverride && (
              <button type="button" onClick={() => setStartDateOverride('')} className="mt-1 text-xs text-gray-400 hover:text-gray-600">
                Clear override
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date Override</label>
            <p className="text-xs text-gray-400 mb-1">Program default: {program?.endDate || 'none'}</p>
            <input
              type="date"
              value={endDateOverride}
              onChange={(e) => setEndDateOverride(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {endDateOverride && (
              <button type="button" onClick={() => setEndDateOverride('')} className="mt-1 text-xs text-gray-400 hover:text-gray-600">
                Clear override
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Archive Status Override</label>
            <p className="text-xs text-gray-400 mb-1">Program default: {program?.isArchived ? 'Archived' : 'Active'}</p>
            <select
              value={isArchivedOverride}
              onChange={(e) => setIsArchivedOverride(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="inherit">Inherit from program</option>
              <option value="active">Override: Active</option>
              <option value="archived">Override: Archived</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : 'Save Overrides'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LocationManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [partnerClients, setPartnerClients] = useState([]);
  const [partnerClientsLoading, setPartnerClientsLoading] = useState(true);
  const [locationPrograms, setLocationPrograms] = useState([]);
  const [locationProgramsLoading, setLocationProgramsLoading] = useState(true);
  const [programTab, setProgramTab] = useState('active');
  const [showAddProgramModal, setShowAddProgramModal] = useState(false);
  const [editingLocationProgram, setEditingLocationProgram] = useState(null);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);

  const fetchLocation = useCallback(async () => {
    try {
      setLocationLoading(true);
      const { data } = await bhcalc.get(`/location/${id}`);
      setLocation(data);
    } catch (err) {
      console.error('Error fetching location:', err);
    } finally {
      setLocationLoading(false);
    }
  }, [id]);

  const fetchPartners = useCallback(async () => {
    try {
      setPartnersLoading(true);
      const { data } = await bhcalc.get('/partner');
      const locationPartners = data.filter(partner => partner.locationId === parseInt(id));
      setPartners(locationPartners);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setPartnersLoading(false);
    }
  }, [id]);

  const fetchPartnerClients = useCallback(async () => {
    try {
      setPartnerClientsLoading(true);
      const { data } = await bhcalc.get('/partner-client');
      const locationPartnerClients = data.filter(client => client.locationId === parseInt(id));
      setPartnerClients(locationPartnerClients);
    } catch (err) {
      console.error('Error fetching partner clients:', err);
    } finally {
      setPartnerClientsLoading(false);
    }
  }, [id]);

  const fetchLocationPrograms = useCallback(async () => {
    try {
      setLocationProgramsLoading(true);
      const { data } = await bhcalc.get(`/program-location?locationId=${id}`);
      setLocationPrograms(data);
    } catch (err) {
      console.error('Error fetching location programs:', err);
    } finally {
      setLocationProgramsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLocation();
    fetchPartners();
    fetchPartnerClients();
    fetchLocationPrograms();
  }, [fetchLocation, fetchPartners, fetchPartnerClients, fetchLocationPrograms]);

  const today = new Date().toISOString().slice(0, 10);

  const isEffectivelyArchived = (lp) => {
    if (lp.isArchivedOverride !== null && lp.isArchivedOverride !== undefined) {
      return lp.isArchivedOverride;
    }
    if (lp.program?.isArchived) return true;
    const effectiveEndDate = lp.endDateOverride ?? lp.program?.endDate;
    if (effectiveEndDate && effectiveEndDate < today) return true;
    return false;
  };

  const activePrograms = locationPrograms.filter(lp => !isEffectivelyArchived(lp));
  const archivedPrograms = locationPrograms.filter(lp => isEffectivelyArchived(lp));
  const displayedPrograms = programTab === 'active' ? activePrograms : archivedPrograms;

  const handleQuickArchive = async (lp, archive) => {
    try {
      await bhcalc.put(`/program-location/${lp.id}`, {
        startDateOverride: lp.startDateOverride || null,
        endDateOverride: lp.endDateOverride || null,
        isArchivedOverride: archive,
      });
      fetchLocationPrograms();
    } catch (err) {
      console.error('Error updating archive status:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="App min-h-screen">
      {showAddProgramModal && (
        <AddProgramModal
          locationId={id}
          onClose={() => setShowAddProgramModal(false)}
          onAdded={() => {
            setShowAddProgramModal(false);
            fetchLocationPrograms();
          }}
        />
      )}

      {showEditLocationModal && location && (
        <EditLocationModal
          location={location}
          onClose={() => setShowEditLocationModal(false)}
          onSaved={() => {
            setShowEditLocationModal(false);
            fetchLocation();
          }}
        />
      )}

      {editingLocationProgram && (
        <EditProgramLocationModal
          locationProgram={editingLocationProgram}
          onClose={() => setEditingLocationProgram(null)}
          onSaved={() => {
            setEditingLocationProgram(null);
            fetchLocationPrograms();
          }}
        />
      )}

      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <div className="flex flex-col items-start mb-6">
              <button
                onClick={() => navigate('/admin')}
                className="text-blue-600 hover:text-blue-800 mb-2"
              >
                &larr; Back to Admin
              </button>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <GiWeightLiftingUp />
                {locationLoading ? 'Loading...' : `${location?.name || 'Unknown'}`}
              </h2>
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Location Details</h3>
                {!locationLoading && location && (
                  <button
                    onClick={() => setShowEditLocationModal(true)}
                    className="px-3 py-1 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-left">
                    {locationLoading ? (
                      <tr>
                        <td colSpan="2" className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading location...
                        </td>
                      </tr>
                    ) : !location ? (
                      <tr>
                        <td colSpan="2" className="px-6 py-4 text-center text-sm text-gray-500">
                          Location not found.
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Name</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.name}</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Address</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.address}</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Zipcode</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.zipcode}</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Logo URL</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.logoUrl || '-'}</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Created</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(location.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Partners at this Location</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">First Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Last Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-left">
                    {partnersLoading ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading partners...
                        </td>
                      </tr>
                    ) : partners.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                          No partners found at this location.
                        </td>
                      </tr>
                    ) : (
                      partners.map((partner) => (
                        <tr key={partner.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{partner.firstName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.lastName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                            <button className="text-red-600 hover:text-red-900">Remove</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {partners.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">
                    Showing {partners.length} partner{partners.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Clients at this Location</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">First Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Last Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Created On</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-left">
                    {partnerClientsLoading ? (
                      <tr>
                        <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading clients...
                        </td>
                      </tr>
                    ) : partnerClients.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                          No clients found at this location.
                        </td>
                      </tr>
                    ) : (
                      partnerClients.map((client) => (
                        <tr key={client.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.firstName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.lastName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(client.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {partnerClients.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">
                    Showing {partnerClients.length} client{partnerClients.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Programs</h3>
                <button
                  onClick={() => setShowAddProgramModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  + Add Program
                </button>
              </div>

              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setProgramTab('active')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      programTab === 'active'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Active ({activePrograms.length})
                  </button>
                  <button
                    onClick={() => setProgramTab('archived')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      programTab === 'archived'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Archived ({archivedPrograms.length})
                  </button>
                </nav>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Program Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Archive Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Image</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-left">
                    {locationProgramsLoading ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading programs...
                        </td>
                      </tr>
                    ) : displayedPrograms.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                          No {programTab} programs found.
                        </td>
                      </tr>
                    ) : (
                      displayedPrograms.map((lp) => (
                        <tr key={lp.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lp.program?.programName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{lp.program?.programType?.name || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {lp.startDateOverride
                              ? <><span>{lp.startDateOverride}</span><span className="ml-1 text-xs text-blue-500">(override)</span></>
                              : lp.program?.startDate
                                ? <span className="text-gray-400">{lp.program.startDate}</span>
                                : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {lp.endDateOverride
                              ? <><span>{lp.endDateOverride}</span><span className="ml-1 text-xs text-blue-500">(override)</span></>
                              : lp.program?.endDate
                                ? <span className="text-gray-400">{lp.program.endDate}</span>
                                : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {lp.isArchivedOverride !== null && lp.isArchivedOverride !== undefined
                              ? <>
                                  <span>{lp.isArchivedOverride ? 'Archived' : 'Active'}</span>
                                  <span className="ml-1 text-xs text-blue-500">(override)</span>
                                </>
                              : <span className="text-gray-400">{lp.program?.isArchived ? 'Archived' : 'Active'}</span>
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {lp.program?.imageUrl ? (
                              <img src={lp.program.imageUrl} alt={lp.program.programName} className="h-8 w-8 object-cover rounded" />
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => setEditingLocationProgram(lp)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              Edit
                            </button>
                            {isEffectivelyArchived(lp) ? (
                              <button
                                onClick={() => handleQuickArchive(lp, false)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                onClick={() => handleQuickArchive(lp, true)}
                                className="text-yellow-600 hover:text-yellow-900"
                              >
                                Archive
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {displayedPrograms.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">
                    Showing {displayedPrograms.length} {programTab} program{displayedPrograms.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationManage;
